import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, Rating, Counter } from '../../shared/components/Common';
import { SmartImg } from '../../shared/components/Layout';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { addDaysIso, formatPrice, places } from '../../shared/data/common';
import { CHILD_FACTOR } from './data';
import { usePackages } from './PackagesContext';
import PackageEnquiryForm from './PackageEnquiryForm';

const tabs = [['itinerary', 'Itinerary'], ['inclusions', 'Inclusions'], ['hotels', 'Hotels'], ['policies', 'Policies']];
const indianCities = places.filter((p) => p.country === 'India').map((p) => p.city);
const longDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

export default function PackageDetails() {
  const { packageId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const q = useQuery();
  const { getPackage, loading } = usePackages();
  const pkg = getPackage(packageId);
  const [tab, setTab] = useState('itinerary');
  const [fromCity, setFromCity] = useState(q.from && indianCities.includes(q.from) ? q.from : 'Delhi');
  const [date, setDate] = useState(q.month ? `${q.month}-15` : addDaysIso(30));
  const [departureDate, setDepartureDate] = useState('');
  const [adults, setAdults] = useState(Number(q.adults || 2));
  const [children, setChildren] = useState(Number(q.children || 0));

  if (!pkg && loading) return <div className="container section empty"><h2>Loading package…</h2></div>;
  if (!pkg) return <div className="container section empty"><h2>Package not found</h2><Link to="/packages" className="btn btn-primary">Browse holidays</Link></div>;

  const minDate = addDaysIso(4);
  // Group tours run on fixed departures; other packages start on any date the customer picks.
  const upcoming = (pkg.departures || []).filter((d) => d.date >= minDate);
  const departure = pkg.category === 'Group' ? upcoming.find((d) => d.date === departureDate) || upcoming[0] : null;
  const startDate = departure ? departure.date : date < minDate ? minDate : date;
  const pricePerAdult = departure?.pricePerAdult ?? pkg.price;
  const pricePerChild = pkg.childPrice ?? Math.round(pricePerAdult * CHILD_FACTOR);
  const maxTravellers = departure ? Math.min(12, departure.seats) : 12;
  const rooms = Math.ceil(adults / 2);
  const total = pricePerAdult * adults + pricePerChild * children;

  const book = () => navigate('/packages/review', {
    state: {
      summary: {
        packageId: pkg.id, title: pkg.title, image: pkg.image, destination: pkg.destination, nights: pkg.nights,
        cities: pkg.cities, hotels: pkg.hotels, includes: pkg.includes, international: pkg.international,
        fromCity: pkg.includes.includes('Flights') ? fromCity : null, date: startDate,
        returnDate: addDaysIso(pkg.nights, startDate), adults, children, rooms, pricePerAdult, pricePerChild,
      },
      backTo: `/packages/${pkg.id}${search}`,
    },
  });

  return (
    <>
      <FlowBand title={pkg.title} step={1} back={{ to: `/packages/results${search}`, label: 'Back to packages' }} />
      <div className="container">
        <div className="pkg-hero">
          <SmartImg src={pkg.image} alt={pkg.title} />
          <div className="pkg-hero-info">
            <span className="tag">{pkg.theme}</span>
            <h2>{pkg.title}</h2>
            <span>{pkg.nights} Nights / {pkg.nights + 1} Days · {pkg.cities.join(' → ')}</span>
            <Rating value={pkg.rating} reviews={pkg.reviews} />
          </div>
        </div>

        <div className="details-layout">
          <div className="details-main">
            <section className="panel">
              {pkg.overview && <p className="pkg-overview">{pkg.overview}</p>}
              <div className="pkg-includes">
                {pkg.includes.map((i) => <span key={i}><Icon name={i === 'Flights' ? 'flights' : i === 'Transfers' ? 'transfers' : i.includes('Hotel') || i.includes('Villa') ? 'hotels' : 'check'} size={20} />{i}</span>)}
              </div>
              {pkg.category === 'Group' && (
                <ul className="pkg-facts">
                  {pkg.tourManagerIncluded && <li><Icon name="user" size={16} /> Tour manager travels with the group</li>}
                  {(pkg.minGroupSize || pkg.maxGroupSize) && <li><Icon name="user" size={16} /> Group size {pkg.minGroupSize || 1}–{pkg.maxGroupSize || 'any'} travellers</li>}
                  <li><Icon name="calendar" size={16} /> {upcoming.length} upcoming departure{upcoming.length === 1 ? '' : 's'}</li>
                </ul>
              )}
            </section>

            {pkg.customizationOptions?.length > 0 && (
              <section className="panel">
                <h3>Make it yours</h3>
                <p className="muted small">Tell us what you'd like in the enquiry form — you can change any of these.</p>
                <ul className="policy-list check-list">{pkg.customizationOptions.map((o) => <li key={o}>{o}</li>)}</ul>
              </section>
            )}

            <section className="panel">
              <div className="underline-tabs">
                {tabs.map(([k, l]) => <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button>)}
              </div>
              {tab === 'itinerary' && (
                <ol className="itinerary">
                  {!pkg.itinerary.length && <li className="muted">Our holiday expert will share the day-wise plan.</li>}
                  {pkg.itinerary.map((d) => (
                    <li key={d.day}>
                      <span className="day-badge">Day {d.day}</span>
                      <div><b>{d.title}</b>{d.description && <span>{d.description}</span>}<span className="muted small">Meals: {d.meals}</span></div>
                    </li>
                  ))}
                </ol>
              )}
              {tab === 'inclusions' && (
                <div className="incl-grid">
                  <div><h4 className="text-primary">Inclusions</h4><ul className="policy-list check-list">{pkg.inclusionsFull.map((i) => <li key={i}>{i}</li>)}</ul></div>
                  <div><h4 className="text-coral">Exclusions</h4><ul className="policy-list cross-list">{pkg.exclusions.map((i) => <li key={i}>{i}</li>)}</ul></div>
                </div>
              )}
              {tab === 'hotels' && (
                <ul className="hotel-lines">
                  {!pkg.hotels.length && <li className="muted">Hotels will be confirmed by our holiday expert.</li>}
                  {pkg.hotels.map((h, i) => <li key={h}><Icon name="hotels" size={18} /><div><b>{h}</b><span className="muted small">{pkg.cities[i] || pkg.cities[0]} · Standard room with breakfast</span></div></li>)}
                </ul>
              )}
              {tab === 'policies' && (
                <ul className="policy-list">
                  <li>More than 30 days before departure: 10% cancellation charge.</li>
                  <li>15–30 days before departure: 50% cancellation charge.</li>
                  <li>Less than 15 days before departure: non-refundable.</li>
                  {pkg.international && <li>5% TCS is collected on international packages as per Indian tax rules and is refundable via ITR.</li>}
                  <li>Hotel names are indicative; similar category hotels may be provided subject to availability.</li>
                </ul>
              )}
            </section>
          </div>

          <aside className="details-side">
            {pkg.enquiryOnly ? <PackageEnquiryForm pkg={pkg} /> : (
            <div className="panel price-card">
              {pkg.oldPrice && <s className="muted small">{formatPrice(pkg.oldPrice)}</s>}
              <b className="price big">{formatPrice(pricePerAdult)}</b>
              <span className="muted small">per adult on twin sharing</span>
              {pkg.includes.includes('Flights') && (
                <label className="field"><span className="field-label">Departure city</span>
                  <select value={fromCity} onChange={(e) => setFromCity(e.target.value)}>{indianCities.map((c) => <option key={c}>{c}</option>)}</select>
                </label>
              )}
              {pkg.category === 'Group' ? (
                <label className="field"><span className="field-label">Group departure</span>
                  {upcoming.length
                    ? <select value={departure.date} onChange={(e) => setDepartureDate(e.target.value)}>
                        {upcoming.map((d) => <option key={d.date} value={d.date}>{longDate(d.date)} · {d.seats} seats</option>)}
                      </select>
                    : <span className="api-message warning">No upcoming departures — please check back soon.</span>}
                </label>
              ) : (
                <label className="field"><span className="field-label">Departure date</span>
                  <input type="date" value={date} min={minDate} onChange={(e) => setDate(e.target.value)} />
                </label>
              )}
              <Counter label="Adults" hint="12 yrs+" value={adults} min={1} max={maxTravellers} onChange={setAdults} />
              <Counter label="Children" hint="2–11 yrs" value={children} min={0} max={Math.max(0, Math.min(6, maxTravellers - adults))} onChange={setChildren} />
              <div className="summary-line total"><span>Total ({rooms} room{rooms > 1 ? 's' : ''})</span><b>{formatPrice(total)}</b></div>
              <span className="muted small">Excl. GST{pkg.international ? ' & TCS' : ''}</span>
              <button className="btn btn-primary block lg" disabled={pkg.category === 'Group' && !departure} onClick={book}>Book Now</button>
            </div>
            )}
          </aside>
        </div>
      </div>
    </>
  );
}
